import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

// Define all available tools
export const VLE_TOOLS = {
  KIOSK_BANKING: "kiosk_banking",
  PIK_VIMA: "pik_vima",
  SETU_MANAGEMENT: "setu_management",
  SETU_SUVIDHA: "setu_suvidha",
  DAILY_BOOK: "daily_book",
} as const;

export type VLEToolKey = (typeof VLE_TOOLS)[keyof typeof VLE_TOOLS];

export interface VLETool {
  tool_key: VLEToolKey;
  is_enabled: boolean;
  enabled_at: string | null;
  disabled_at: string | null;
}

export interface VLEToolsState {
  tools: Record<VLEToolKey, boolean>;
  isLoading: boolean;
  isToolEnabled: (toolKey: VLEToolKey) => boolean;
  enableTool: (toolKey: VLEToolKey) => Promise<void>;
  disableTool: (toolKey: VLEToolKey) => Promise<void>;
  toggleTool: (toolKey: VLEToolKey, enabled: boolean) => Promise<void>;
  refetch: () => void;
}

export function useVLETools(): VLEToolsState {
  const { tenant, user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch all tools for this tenant
  const { data: toolsData, isLoading, refetch } = useQuery({
    queryKey: ["vle_tools", tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];
      
      const { data, error } = await supabase
        .from("vle_tools")
        .select("*")
        .eq("tenant_id", tenant.id);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenant?.id,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Convert array to map for easy lookup
  const toolsMap: Record<VLEToolKey, boolean> = {
    [VLE_TOOLS.KIOSK_BANKING]: false,
    [VLE_TOOLS.PIK_VIMA]: false,
    [VLE_TOOLS.SETU_MANAGEMENT]: false,
    [VLE_TOOLS.SETU_SUVIDHA]: false,
    [VLE_TOOLS.DAILY_BOOK]: true, // Daily Book is enabled by default
  };

  if (toolsData) {
    toolsData.forEach((tool) => {
      if (tool.tool_key in toolsMap) {
        toolsMap[tool.tool_key as VLEToolKey] = tool.is_enabled;
      }
    });
  }

  // Check if a tool is enabled
  const isToolEnabled = (toolKey: VLEToolKey): boolean => {
    return toolsMap[toolKey] ?? false;
  };

  // Enable tool mutation
  const enableMutation = useMutation({
    mutationFn: async (toolKey: VLEToolKey) => {
      if (!tenant?.id || !user?.id) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from("vle_tools")
        .upsert({
          tenant_id: tenant.id,
          tool_key: toolKey,
          is_enabled: true,
          enabled_at: new Date().toISOString(),
          disabled_at: null,
        }, {
          onConflict: "tenant_id,tool_key",
        });
      
      if (error) throw error;
    },
    onSuccess: (_, toolKey) => {
      queryClient.invalidateQueries({ queryKey: ["vle_tools"] });
      queryClient.invalidateQueries({ queryKey: ["setu_sidebar_access"] }); // For Setu sidebar
      toast({ title: `Tool enabled successfully` });
    },
    onError: (error: Error) => {
      toast({ 
        title: "Failed to enable tool", 
        description: error.message, 
        variant: "destructive" 
      });
    },
  });

  // Disable tool mutation
  const disableMutation = useMutation({
    mutationFn: async (toolKey: VLEToolKey) => {
      if (!tenant?.id) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from("vle_tools")
        .upsert({
          tenant_id: tenant.id,
          tool_key: toolKey,
          is_enabled: false,
          disabled_at: new Date().toISOString(),
        }, {
          onConflict: "tenant_id,tool_key",
        });
      
      if (error) throw error;
    },
    onSuccess: (_, toolKey) => {
      queryClient.invalidateQueries({ queryKey: ["vle_tools"] });
      queryClient.invalidateQueries({ queryKey: ["setu_sidebar_access"] }); // For Setu sidebar
      toast({ title: `Tool disabled` });
    },
    onError: (error: Error) => {
      toast({ 
        title: "Failed to disable tool", 
        description: error.message, 
        variant: "destructive" 
      });
    },
  });

  const enableTool = async (toolKey: VLEToolKey) => {
    await enableMutation.mutateAsync(toolKey);
  };

  const disableTool = async (toolKey: VLEToolKey) => {
    await disableMutation.mutateAsync(toolKey);
  };

  const toggleTool = async (toolKey: VLEToolKey, enabled: boolean) => {
    if (enabled) {
      await enableTool(toolKey);
    } else {
      await disableTool(toolKey);
    }
  };

  return {
    tools: toolsMap,
    isLoading,
    isToolEnabled,
    enableTool,
    disableTool,
    toggleTool,
    refetch,
  };
}
